import { Link, Outlet, createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { Building2, MessageCircle, Send, Settings, Users, Webhook } from 'lucide-react';
import { useEffect } from 'react';

import { useWorkspaceRole } from '@/lib/hooks/useWorkspaceRole';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/$wsSlug/settings')({
  component: SettingsLayout,
});

// Tabs admin-only: configuracion del workspace en si.
const ADMIN_TABS = [
  { to: 'general', label: 'General', icon: Settings },
  { to: 'members', label: 'Miembros', icon: Users },
  { to: 'integrations', label: 'Integraciones', icon: Webhook },
  { to: 'areas', label: 'Areas', icon: Building2 },
] as const;

// Tabs user-level: cada miembro vincula sus propios canales personales.
const USER_TABS = [
  { to: 'telegram', label: 'Telegram', icon: Send },
  { to: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
] as const;

function SettingsLayout() {
  const { wsSlug } = Route.useParams();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const { canManage, isLoading } = useWorkspaceRole(wsSlug);

  // Si el user llega a /settings sin sub-path, mandarlo al primer tab que
  // pueda ver. Admin/owner -> general; miembro/guest -> whatsapp (su unica
  // config personal accesible aca).
  useEffect(() => {
    if (isLoading) return;
    if (pathname === `/${wsSlug}/settings`) {
      const target = canManage ? 'general' : 'whatsapp';
      navigate({
        to: `/$wsSlug/settings/${target}` as never,
        params: { wsSlug } as never,
        replace: true,
      });
    }
  }, [pathname, wsSlug, canManage, isLoading, navigate]);

  // Admin/owner ven todos los tabs; miembro/guest solo los user-level. El
  // gate de admin se aplica per-tab dentro de cada pagina admin-only como
  // defense-in-depth (RoleGate dentro de general/members/integrations).
  const tabs = canManage ? [...ADMIN_TABS, ...USER_TABS] : [...USER_TABS];

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="px-6 pt-6 pb-0 border-b border-border">
        <h1 className="text-xl font-semibold text-ink mb-4">Configuracion</h1>

        {/* Tabs */}
        <nav className="flex gap-0" aria-label="Configuracion tabs">
          {tabs.map(({ to, label, icon: Icon }) => {
            const fullPath = `/${wsSlug}/settings/${to}`;
            const isActive = pathname === fullPath || pathname.startsWith(`${fullPath}/`);
            return (
              <Link
                key={to}
                to={`/$wsSlug/settings/${to}` as never}
                params={{ wsSlug } as never}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 h-9 text-sm font-medium border-b-2 transition-colors',
                  isActive
                    ? 'border-accent text-accent'
                    : 'border-transparent text-quiet hover:text-body hover:border-border',
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon size={14} />
                {label}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto p-6">
        <Outlet />
      </div>
    </div>
  );
}
